import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Card,
  Typography,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  Button,
  Snackbar,
  Alert,
  Box,
  Divider,
  Chip,
  Stack,
  Paper,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VerifiedIcon from "@mui/icons-material/Verified";
import BlockIcon from "@mui/icons-material/Block";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import BoltIcon from "@mui/icons-material/Bolt";
import api from "../../utils/api";

const STATUS_OPTIONS = [
  { label: "Draft", value: "Draft" },
  { label: "Upcoming", value: "Upcoming" },
  { label: "Active", value: "Active" },
  { label: "Closed", value: "Closed" },
  { label: "Archived", value: "Archived" },
];

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const sqlDateFormat = (dt) => {
  if (!dt) return null;
  const date = new Date(dt);
  return date.toISOString().slice(0, 19).replace("T", " ");
};

const SettingsTab = ({ election, onDelete, onElectionUpdated }) => {
  const [form, setForm] = useState({
    title: "",
    description: "",
    start_datetime: "",
    end_datetime: "",
    status: "Draft",
    is_anonymous: true,
    allow_vote_change: false,
    show_results: false,
    require_pin: false,
    max_votes_per_member: 1,
    property_based_voting: false,
    fs_718128_compliance: false,
  });

  const [saving, setSaving]           = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const hasEditedRef = useRef(false);

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const handleChange = (field, value) => {
    hasEditedRef.current = true;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (!election) return;
    if (hasEditedRef.current) return;

    setForm({
      title: election.title || "",
      description: election.description || "",
      start_datetime: election.start_datetime
        ? String(election.start_datetime).slice(0, 16)
        : "",
      end_datetime: election.end_datetime
        ? String(election.end_datetime).slice(0, 16)
        : "",
      status: election.status || "Draft",
      is_anonymous: Number(election.is_anonymous) === 1,
      allow_vote_change: Number(election.allow_vote_change) === 1,
      show_results: Number(election.show_results) === 1,
      require_pin: Number(election.require_pin) === 1,
      max_votes_per_member: Number(election.max_votes_per_member ?? 1),
      property_based_voting: Number(election.property_based_voting) === 1,
      fs_718128_compliance: election.fs_718128_compliance === true || Number(election.fs_718128_compliance) === 1,
    });
  }, [election]);

  const startDate = useMemo(
    () => (form.start_datetime ? new Date(form.start_datetime) : null),
    [form.start_datetime]
  );

  const endDate = useMemo(
    () => (form.end_datetime ? new Date(form.end_datetime) : null),
    [form.end_datetime]
  );

  const now = useMemo(
    () => new Date(),
    [form.start_datetime, form.end_datetime, form.status]
  );

  const dateRangeValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    return startDate <= endDate;
  }, [startDate, endDate]);

  const accessState = useMemo(() => {
    const status = String(form.status || "").toLowerCase();
    const hasDates = !!startDate && !!endDate;
    const inWindow = hasDates ? now >= startDate && now <= endDate : false;

    const votingAllowed = status === "active" && hasDates && inWindow;
    const sharingAllowed = (status === "active" || status === "upcoming") && hasDates;
    const resultsVisible =
      !!form.show_results || status === "closed" || status === "archived";

    return { status, hasDates, inWindow, votingAllowed, sharingAllowed, resultsVisible };
  }, [form.status, form.show_results, startDate, endDate, now]);

  const handleSave = async () => {
    if (!election?.id) return;

    if (form.start_datetime && form.end_datetime && !dateRangeValid) {
      showSnackbar("End Date must be after Start Date.", "error");
      return;
    }

    setSaving(true);
    try {
      await api.put(`/elections/${election.id}`, {
        title: form.title,
        description: form.description,
        start_datetime: sqlDateFormat(form.start_datetime),
        end_datetime: sqlDateFormat(form.end_datetime),
        status: form.status,
        is_anonymous: form.is_anonymous ? 1 : 0,
        allow_vote_change: form.allow_vote_change ? 1 : 0,
        show_results: form.show_results ? 1 : 0,
        require_pin: form.require_pin ? 1 : 0,
        max_votes_per_member: Number(form.max_votes_per_member ?? 1),
        property_based_voting: form.property_based_voting ? 1 : 0,
        fs_718128_compliance: form.fs_718128_compliance ? 1 : 0,
      });

      const fresh = await api.get(`/elections/${election.id}`);
      onElectionUpdated?.(fresh.data);

      hasEditedRef.current = false;
      showSnackbar("Election settings saved!", "success");
    } catch (error) {
      console.error("Failed to save settings:", error);
      showSnackbar("Failed to save election settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!election?.id) return;
    setDeleteDialogOpen(false);
    try {
      await api.delete(`/elections/${election.id}`);
      showSnackbar("Election deleted successfully!", "success");
      setTimeout(() => onDelete && onDelete(), 300);
    } catch (error) {
      console.error("Failed to delete election:", error);
      showSnackbar("Failed to delete election", "error");
    }
  };

  const setActiveNow = () => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 7);
    handleChange("status", "Active");
    handleChange("start_datetime", start.toISOString().slice(0, 16));
    handleChange("end_datetime", end.toISOString().slice(0, 16));
  };

  return (
    <>
      <Box
        sx={{
          display: "grid",
          gap: 2.5,
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 2fr) minmax(320px, 1fr)" },
          alignItems: "start",
        }}
      >
        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
              >
                Election Settings
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b778c" }}>
                Configure schedule, voting rules, privacy, and results visibility.
              </Typography>
            </Box>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <Button
                variant="outlined"
                startIcon={<BoltIcon />}
                onClick={setActiveNow}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Set Active (1 week)
              </Button>

              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSave}
                disabled={saving}
                sx={{
                  backgroundColor: "#2f63ad",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#275592",
                    boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                  },
                }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <Typography
            variant="subtitle2"
            sx={{
              mb: 1.5,
              fontWeight: 800,
              color: "#223142",
              letterSpacing: 0.2,
            }}
          >
            Basic
          </Typography>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Election Title"
                value={form.title}
                onChange={(e) => handleChange("title", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Description"
                value={form.description}
                onChange={(e) => handleChange("description", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  mt: 1,
                  mb: 1.5,
                  fontWeight: 800,
                  color: "#223142",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <AccessTimeIcon fontSize="small" />
                Schedule & Status
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Status"
                value={form.status}
                onChange={(e) => handleChange("status", e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Max votes per member"
                value={form.max_votes_per_member}
                onChange={(e) =>
                  handleChange("max_votes_per_member", Number(e.target.value))
                }
                inputProps={{ min: 1, step: 1 }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={form.start_datetime}
                onChange={(e) => handleChange("start_datetime", e.target.value)}
                error={!!form.end_datetime && !!form.start_datetime && !dateRangeValid}
                helperText={
                  !!form.end_datetime && !!form.start_datetime && !dateRangeValid
                    ? "Start must be before End."
                    : " "
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="datetime-local"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={form.end_datetime}
                onChange={(e) => handleChange("end_datetime", e.target.value)}
                error={!!form.end_datetime && !!form.start_datetime && !dateRangeValid}
                helperText={
                  !!form.end_datetime && !!form.start_datetime && !dateRangeValid
                    ? "End must be after Start."
                    : " "
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 0.5 }} />
              <Typography
                variant="subtitle2"
                sx={{
                  mt: 1,
                  mb: 1.5,
                  fontWeight: 800,
                  color: "#223142",
                }}
              >
                Voting Options
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.is_anonymous}
                      onChange={(e) => handleChange("is_anonymous", e.target.checked)}
                    />
                  }
                  label="Anonymous Voting"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.allow_vote_change}
                      onChange={(e) =>
                        handleChange("allow_vote_change", e.target.checked)
                      }
                    />
                  }
                  label="Allow Vote Changing"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.show_results}
                      onChange={(e) => handleChange("show_results", e.target.checked)}
                    />
                  }
                  label="Show Results Automatically"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.require_pin}
                      onChange={(e) => handleChange("require_pin", e.target.checked)}
                    />
                  }
                  label="Require PIN for Voting"
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: "1px solid rgba(47,99,173,0.08)",
                  backgroundColor: "rgba(47,99,173,0.02)",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.property_based_voting}
                      onChange={(e) =>
                        handleChange("property_based_voting", e.target.checked)
                      }
                    />
                  }
                  label="Property-based voting"
                />
              </Paper>
            </Grid>

            {/* FS 718.128 Compliance toggle. full width with explanation */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  border: `1px solid ${form.fs_718128_compliance ? "rgba(21,101,192,0.30)" : "rgba(47,99,173,0.08)"}`,
                  backgroundColor: form.fs_718128_compliance ? "rgba(21,101,192,0.05)" : "rgba(47,99,173,0.02)",
                  transition: "all 0.2s ease",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.fs_718128_compliance}
                      onChange={(e) => handleChange("fs_718128_compliance", e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={700} sx={{ color: "#223142" }}>
                        FS 718.128 Florida Condominium Compliance
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#6b778c", display: "block", mt: 0.25 }}>
                        {form.fs_718128_compliance
                          ? "ON. Voter email and IP address will NOT be collected or displayed. Ballot identity is fully separated. Recommended for board elections."
                          : "OFF. Voter email and IP address are recorded for audit purposes. Suitable for non-board votes and surveys."}
                      </Typography>
                    </Box>
                  }
                  sx={{ alignItems: "flex-start", gap: 1 }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1.5 }} />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, color: "#223142" }}>
                    Danger zone
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b778c" }}>
                    This permanently deletes the election and related data.
                  </Typography>
                </Box>

                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Delete Election
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Card>

        <Paper
          elevation={0}
          sx={{
            ...adminCardSx,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2.5,
              backgroundColor: "rgba(47,99,173,0.02)",
              borderBottom: "1px solid rgba(47,99,173,0.08)",
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                color: "#223142",
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <VerifiedIcon fontSize="small" />
              Access Rules
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Voting, sharing, and results visibility should follow status and schedule.
            </Typography>
          </Box>

          <Box sx={{ p: 2.5 }}>
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ color: "#6b778c" }}>Status</Typography>
                <Chip label={form.status} />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ color: "#6b778c" }}>Has start / end</Typography>
                <Chip
                  label={accessState.hasDates ? "Yes" : "No"}
                  color={accessState.hasDates ? "success" : "default"}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ color: "#6b778c" }}>In voting window</Typography>
                <Chip
                  label={accessState.inWindow ? "Yes" : "No"}
                  color={accessState.inWindow ? "success" : "default"}
                />
              </Box>

              <Divider />

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                  Share link / invites
                </Typography>
                <Chip
                  icon={accessState.sharingAllowed ? <VerifiedIcon /> : <BlockIcon />}
                  label={accessState.sharingAllowed ? "Allowed" : "Blocked"}
                  color={accessState.sharingAllowed ? "success" : "default"}
                  variant={accessState.sharingAllowed ? "filled" : "outlined"}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                  Voting
                </Typography>
                <Chip
                  icon={accessState.votingAllowed ? <VerifiedIcon /> : <BlockIcon />}
                  label={accessState.votingAllowed ? "Allowed" : "Blocked"}
                  color={accessState.votingAllowed ? "success" : "default"}
                  variant={accessState.votingAllowed ? "filled" : "outlined"}
                />
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
                <Typography sx={{ fontWeight: 700, color: "#223142" }}>
                  Results
                </Typography>
                <Chip
                  label={accessState.resultsVisible ? "Visible" : "Hidden"}
                  color={accessState.resultsVisible ? "info" : "default"}
                  variant={accessState.resultsVisible ? "filled" : "outlined"}
                />
              </Box>
            </Stack>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{ sx: { borderRadius: "16px", p: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: "#223142" }}>
          Delete Election?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#5a6578" }}>
            This will permanently delete <strong>{election?.title}</strong> along
            with all its questions, invites, and vote records. This action cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 600,
              color: "#5a6578",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsTab;
